/**
 * Renders a Stage 10 explanation object as an ordered list of message
 * keys drawn from the frontend registry. Never renders raw backend
 * text; unknown keys fall back to a controlled placeholder. Includes
 * confidence, severity, and citation counts for transparency.
 */
import { renderMessage, isRegisteredMessage }
  from "@/lib/coachingMessageRegistry";
import { formatConfidence, formatSeverity, severityToken,
              formatSport, formatSessionRole, formatObjective,
              formatLimiter, formatPrescriptionMethod }
  from "@/lib/coachingFormatters";

const PARAM_FORMATTERS = {
  sport: formatSport,
  primary_sport: formatSport,
  role: formatSessionRole,
  objective: formatObjective,
  limiter: formatLimiter,
  prescription_method: formatPrescriptionMethod,
};

const normaliseParams = (params) => {
  const out = { ...(params || {}) };
  for (const key of Object.keys(out)) {
    const fmt = PARAM_FORMATTERS[key];
    if (fmt && out[key] != null && out[key] !== "") {
      out[key] = fmt(out[key]);
    }
  }
  return out;
};

const severityRing = {
  critical: "border-l-4 border-analytics-red",
  high: "border-l-4 border-analytics-amber",
  warning: "border-l-4 border-analytics-amber",
  notice: "border-l-4 border-analytics-blue",
  info: "border-l-4 border-dark-border",
};

export const CoachingExplanation = ({ explanation, showMeta = true,
                                                    className = "",
                                                    testId }) => {
  if (!explanation) return null;
  const messageKeys = Array.isArray(explanation.message_keys)
    ? explanation.message_keys : [];
  const params = normaliseParams(explanation.parameters);
  const token = severityToken(explanation.severity);

  return (
    <div
      className={`text-sm text-dark-text-primary ${severityRing[token]
                                                                        || ""} pl-3 ${className}`}
      data-testid={testId}
    >
      <ul className="space-y-1.5" role="list">
        {messageKeys.length === 0 ? (
          <li className="text-dark-text-secondary">No details available.</li>
        ) : (
          messageKeys.map((mk) => {
            const { text, isFallback } = renderMessage(mk, params);
            return (
              <li key={mk}
                    className={isFallback ? "text-dark-text-muted"
                                                 : "text-dark-text-primary"}
                    data-testid={`msg-${mk}`}>
                {text}
              </li>
            );
          })
        )}
      </ul>
      {showMeta && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-widest text-dark-text-muted">
          {explanation.confidence && (
            <span aria-label="Explanation confidence">
              {formatConfidence(explanation.confidence)}
            </span>
          )}
          {explanation.severity && explanation.severity !== "INFO" && (
            <span aria-label="Explanation severity">
              {formatSeverity(explanation.severity)}
            </span>
          )}
          {Array.isArray(explanation.source_citations)
              && explanation.source_citations.length > 0 && (
            <span aria-label="Number of source citations">
              {explanation.source_citations.length} source(s)
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const _internal = { isRegisteredMessage };
